/**
 * TypeScript types for Lead Management System
 * 
 * These types match the backend Pydantic schemas in backend/app/schemas/lead.py
 * 
 * Requirements: 1.1, 2.1, 4.6
 */

/**
 * Lead status enumeration
 * Represents the current state of a lead in the sales pipeline
 */
export enum LeadStatus {
  NEW = "new",
  CONTACTED = "contacted",
  QUALIFIED = "qualified",
  PROPOSAL_SENT = "proposal_sent",
  NEGOTIATION = "negotiation",
  CONVERTED = "converted",
  LOST = "lost"
}

/**
 * Lead source enumeration
 * Represents the origin of the lead
 */
export enum LeadSource {
  INDIAMART = "indiamart",
  INSTAGRAM = "instagram",
  FACEBOOK_ADS = "facebook_ads",
  MANUAL_ENTRY = "manual_entry",
  WEBSITE = "website",
  REFERRAL = "referral",
  SOCIAL_MEDIA = "social_media",
  EMAIL_CAMPAIGN = "email_campaign",
  TRADE_SHOW = "trade_show",
  COLD_CALL = "cold_call",
  OTHER = "other"
}

/**
 * GST verification status enumeration
 * Represents the verification state of a lead's GST number
 */
export enum GSTVerificationStatus {
  VERIFIED = "verified",
  UNVERIFIED = "unverified",
  PENDING = "pending",
  NOT_APPLICABLE = "not_applicable"
}

/**
 * Base lead interface with all core fields
 */
export interface LeadBase {
  name: string;
  email?: string | null;
  phone: string;
  company?: string | null;
  gst_number?: string | null;
  location?: string | null;
  city?: string | null;
  state?: string | null;
  source: LeadSource;
  interest_category?: string | null;
  status: LeadStatus;
  campaign_tags: string[];
  notes?: string | null;
}

/**
 * Lead creation request interface
 * Used when creating a new lead via API
 */
export interface LeadCreate extends LeadBase {}

/**
 * Lead update request interface
 * All fields are optional for partial updates
 */
export interface LeadUpdate {
  name?: string;
  email?: string | null;
  phone?: string;
  company?: string | null;
  gst_number?: string | null;
  location?: string | null;
  city?: string | null;
  state?: string | null;
  source?: LeadSource;
  interest_category?: string | null;
  status?: LeadStatus;
  campaign_tags?: string[];
  notes?: string | null;
  is_active?: boolean;
}

/**
 * Complete lead interface with system-generated fields
 * Used for displaying lead data from API responses
 */
export interface Lead extends LeadBase {
  id: string;
  gst_verification_status: GSTVerificationStatus;
  gst_verified_at?: string | null;
  is_active: boolean;
  last_contacted_at?: string | null;
  created_at: string;
  updated_at: string;
  // Additional fields used in UI
  query?: string | null;  // Query details
}

/**
 * Paginated lead list response interface
 */
export interface LeadListResponse {
  items: Lead[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

/**
 * Lead filtering criteria interface
 * Used for segmentation and search
 */
export interface LeadFilters {
  gst_verification_status?: GSTVerificationStatus[];
  location?: string;
  city?: string;
  state?: string;
  source?: LeadSource[];
  interest_category?: string;
  status?: LeadStatus[];
  campaign_tag?: string;
  date_from?: string; // ISO date string (YYYY-MM-DD)
  date_to?: string; // ISO date string (YYYY-MM-DD)
  search?: string;
  is_active?: boolean;
}

/**
 * Bulk import request interface
 */
export interface BulkImportRequest {
  file_content: string; // Base64 encoded CSV/Excel
  file_type: "csv" | "excel";
}

/**
 * Bulk import result interface
 */
export interface BulkImportResult {
  total_rows: number;
  successful: number;
  failed: number;
  errors: Array<{
    row?: number;
    error: string;
    [key: string]: any;
  }>;
}

/**
 * Saved segment creation request interface
 */
export interface SegmentCreate {
  name: string;
  description?: string | null;
  filter_criteria: Record<string, any>;
}

/**
 * Saved segment update request interface
 */
export interface SegmentUpdate {
  name?: string;
  description?: string | null;
  filter_criteria?: Record<string, any>;
}

/**
 * Saved segment response interface
 */
export interface LeadSegment {
  id: string;
  name: string;
  description?: string | null;
  filter_criteria: Record<string, any>;
  created_by: string;
  created_at: string;
  is_active: boolean;
}

/**
 * Helper type for lead sorting fields
 */
export type LeadSortField = 
  | "created_at"
  | "updated_at"
  | "name"
  | "company"
  | "status"
  | "last_contacted_at";

/**
 * Helper type for sort order
 */
export type SortOrder = "asc" | "desc";

/**
 * Lead list query parameters interface
 * Used for API requests with pagination, filtering, and sorting
 */
export interface LeadListParams {
  page?: number;
  per_page?: number;
  gst_verification_status?: GSTVerificationStatus[];
  location?: string;
  city?: string;
  state?: string;
  source?: LeadSource[];
  interest_category?: string;
  status?: LeadStatus[];
  campaign_tag?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  sort_by?: LeadSortField;
  sort_order?: SortOrder;
}

/**
 * Lead export query parameters interface
 */
export interface LeadExportParams {
  gst_verification_status?: GSTVerificationStatus[];
  location?: string;
  city?: string;
  state?: string;
  source?: LeadSource[];
  interest_category?: string;
  status?: LeadStatus[];
  campaign_tag?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
}
